import React, { useEffect, useState } from "react";
import { firestore } from "./firebase";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Papa from "papaparse";

function UsersTable({ onLogout }) {
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedSports, setSelectedSports] = useState(null);
  const [selectedSportsUserId, setSelectedSportsUserId] = useState(null);
  const [playersData, setPlayersData] = useState([]);
  const [activeTab, setActiveTab] = useState("users");
  const [selectedCollection, setSelectedCollection] = useState("Users25");
  const [isEditing, setIsEditing] = useState(false);
  const [editedSports, setEditedSports] = useState(null);
  const [editingField, setEditingField] = useState({});
  const [tempValues, setTempValues] = useState({});

  useEffect(() => {
    fetchData();
  }, [selectedCollection]);

  const fetchData = async () => {
    try {
      const snapshot = await getDocs(collection(firestore, selectedCollection));
      let docsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      docsData.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;

        if (!aTime && !bTime) return 0;
        if (!aTime) return 1;
        if (!bTime) return -1;
        return bTime - aTime;
      });

      setData(docsData);
      if (docsData.length > 0) {
        setHeaders(
          Object.keys(docsData[0]).filter(
            (key) => key !== "createdAt" && key !== "updatedAt"
          )
        );
      }

      const allPlayers = [];
      docsData.forEach((user) => {
        if (user.sports && typeof user.sports === "object") {
          Object.entries(user.sports).forEach(([sportName, sportInfo]) => {
            if (sportInfo.team && Array.isArray(sportInfo.team)) {
              sportInfo.team.forEach((player, idx) => {
                allPlayers.push({
                  userId: user.id,
                  institute: user.institute || "",
                  instituteAddress: user.instituteAddress || "",
                  sport: sportName,
                  player: player.player || "",
                  number: player.number || "",
                  contact: player.contact || "",
                  role:
                    idx === 0
                      ? "Captain"
                      : idx === 1
                        ? "Vice Captain"
                        : "Member",
                  captainName: `${sportInfo.firstNameCaptain || ""} ${sportInfo.lastNameCaptain || ""}`.trim(),
                  captainEmail: sportInfo.emailCaptain || "",
                  captainContact: sportInfo.contactCaptain || "",
                  viceCaptainName: `${sportInfo.firstNameViceCaptain || ""} ${sportInfo.lastNameViceCaptain || ""}`.trim(),
                  viceCaptainEmail: sportInfo.emailViceCaptain || "",
                  viceCaptainContact: sportInfo.contactViceCaptain || "",
                });
              });
            }
          });
        }
      });
      setPlayersData(allPlayers);
      
      setEditingField({});
      setTempValues({});
    } catch (error) {
      toast.error("Error fetching data: " + error.message);
    }
  };

  const handleLogoutClick = () => {
    if (onLogout && typeof onLogout === 'function') {
      onLogout();
    }
  };

  const handleFilterChange = (header, value) => {
    setFilters((prev) => ({ ...prev, [header]: value }));
  };

  const filteredData = data.filter((row) =>
    headers.every((header) =>
      filters[header]
        ? String(row[header] ?? "")
          .toLowerCase()
          .includes(filters[header].toLowerCase())
        : true
    )
  );

  const handleViewSports = (sportsData, userId) => {
    setSelectedSports(sportsData);
    setSelectedSportsUserId(userId);
    setEditedSports(JSON.parse(JSON.stringify(sportsData)));
    setIsEditing(false);
  };

  const handleEditChange = (sportName, field, value, isTeamMember = false, index = null) => {
    setEditedSports(prev => {
      const updated = {...prev};
      
      if (isTeamMember && index !== null) {
        updated[sportName].team[index][field] = value;
      } else {
        updated[sportName][field] = value;
      }
      
      return updated;
    });
  };

  const updateCustomField = async (userId, field, value) => {
    try {
      const userDoc = doc(firestore, selectedCollection, userId);
      await updateDoc(userDoc, {
        [field]: value
      });
      
      setData(prevData => 
        prevData.map(item => 
          item.id === userId 
            ? {...item, [field]: value} 
            : item
        )
      );
      
      toast.success(`${field} updated successfully!`);
    } catch (error) {
      toast.error("Error updating data: " + error.message);
    }
  };

  const toggleYesNo = (userId, currentValue) => {
    updateCustomField(userId, 'confirmed', !currentValue);
  };

  const handleResponseChange = (userId, value) => {
    setTempValues(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        response: value
      }
    }));
  };

  const handlePocChange = (userId, value) => {
    setTempValues(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        poc: value
      }
    }));
  };

  const saveResponse = (userId) => {
    const value = tempValues[userId]?.response || "";
    updateCustomField(userId, 'response', value);
    setEditingField(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        response: false
      }
    }));
  };

  const savePoc = (userId) => {
    const value = tempValues[userId]?.poc || "";
    updateCustomField(userId, 'poc', value);
    setEditingField(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        poc: false
      }
    }));
  };

  const clearResponse = (userId) => {
    updateCustomField(userId, 'response', "");
  };

  const clearPoc = (userId) => {
    updateCustomField(userId, 'poc', "");
  };

  const startEditing = (userId, field) => {
    setEditingField(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: true
      }
    }));
    
    const currentRow = data.find(item => item.id === userId);
    const currentValue = currentRow ? currentRow[field] || "" : "";
    
    setTempValues(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: currentValue
      }
    }));
  };

  const cancelEditing = (userId, field) => {
    setEditingField(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: false
      }
    }));
  };

  const saveChanges = async () => {
    try {
      const userDoc = doc(firestore, selectedCollection, selectedSportsUserId);
      await updateDoc(userDoc, {
        sports: editedSports
      });
      
      setData(prevData => 
        prevData.map(item => 
          item.id === selectedSportsUserId 
            ? {...item, sports: editedSports} 
            : item
        )
      );
      
      const updatedPlayersData = [];
      data.forEach((user) => {
        if (user.id === selectedSportsUserId && user.sports) {
          Object.entries(user.sports).forEach(([sportName, sportInfo]) => {
            if (sportInfo.team && Array.isArray(sportInfo.team)) {
              sportInfo.team.forEach((player, idx) => {
                updatedPlayersData.push({
                  userId: user.id,
                  institute: user.institute || "",
                  instituteAddress: user.instituteAddress || "",
                  sport: sportName,
                  player: player.player || "",
                  number: player.number || "",
                  contact: player.contact || "",
                  role: idx === 0 ? "Captain" : idx === 1 ? "Vice Captain" : "Member",
                  captainName: `${sportInfo.firstNameCaptain || ""} ${sportInfo.lastNameCaptain || ""}`.trim(),
                  captainEmail: sportInfo.emailCaptain || "",
                  captainContact: sportInfo.contactCaptain || "",
                  viceCaptainName: `${sportInfo.firstNameViceCaptain || ""} ${sportInfo.lastNameViceCaptain || ""}`.trim(),
                  viceCaptainEmail: sportInfo.emailViceCaptain || "",
                  viceCaptainContact: sportInfo.contactViceCaptain || "",
                });
              });
            }
          });
        } else if (user.sports && typeof user.sports === "object") {
          Object.entries(user.sports).forEach(([sportName, sportInfo]) => {
            if (sportInfo.team && Array.isArray(sportInfo.team)) {
              sportInfo.team.forEach((player, idx) => {
                updatedPlayersData.push({
                  userId: user.id,
                  institute: user.institute || "",
                  instituteAddress: user.instituteAddress || "",
                  sport: sportName,
                  player: player.player || "",
                  number: player.number || "",
                  contact: player.contact || "",
                  role: idx === 0 ? "Captain" : idx === 1 ? "Vice Captain" : "Member",
                  captainName: `${sportInfo.firstNameCaptain || ""} ${sportInfo.lastNameCaptain || ""}`.trim(),
                  captainEmail: sportInfo.emailCaptain || "",
                  captainContact: sportInfo.contactCaptain || "",
                  viceCaptainName: `${sportInfo.firstNameViceCaptain || ""} ${sportInfo.lastNameViceCaptain || ""}`.trim(),
                  viceCaptainEmail: sportInfo.emailViceCaptain || "",
                  viceCaptainContact: sportInfo.contactViceCaptain || "",
                });
              });
            }
          });
        }
      });
      
      setPlayersData(updatedPlayersData);
      setSelectedSports(editedSports);
      setIsEditing(false);
      toast.success("Sports data updated successfully!");
    } catch (error) {
      toast.error("Error updating data: " + error.message);
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteDoc(doc(firestore, selectedCollection, userId));
        setData(prevData => prevData.filter(item => item.id !== userId));
        setPlayersData(prevData => prevData.filter(item => item.userId !== userId));
        toast.success("User deleted successfully!");
      } catch (error) {
        toast.error("Error deleting user: " + error.message);
      }
    }
  };

  const deletePlayer = async (playerData) => {
    if (window.confirm("Are you sure you want to delete this player?")) {
      try {
        const user = data.find(user => user.id === playerData.userId);
        if (!user || !user.sports) return;
        
        const updatedSports = JSON.parse(JSON.stringify(user.sports));
        
        if (updatedSports[playerData.sport] && updatedSports[playerData.sport].team) {
          updatedSports[playerData.sport].team = updatedSports[playerData.sport].team.filter(
            player => !(player.player === playerData.player && player.number === playerData.number)
          );
          
          if (updatedSports[playerData.sport].team.length === 0) {
            delete updatedSports[playerData.sport];
          }
          
          const userDoc = doc(firestore, selectedCollection, playerData.userId);
          await updateDoc(userDoc, {
            sports: updatedSports
          });
          
          fetchData();
          toast.success("Player deleted successfully!");
        }
      } catch (error) {
        toast.error("Error deleting player: " + error.message);
      }
    }
  };

  const exportCSV = () => {
    if (!data.length) return toast.error("No user data to export!");
    const csvData = data.map((user) => {
      let sportsSummary = "";
      if (user.sports && typeof user.sports === "object") {
        sportsSummary = Object.entries(user.sports)
          .map(
            ([sportName, sportInfo]) =>
              `${sportName}-${sportInfo.team?.length || 0}`
          )
          .join(",");
      }
      return { ...user, sports: sportsSummary };
    });
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "users_export.csv";
    link.click();
  };

  const exportPlayersCSV = () => {
    const csv = Papa.unparse(playersData, {
      columns: [
        "userId",
        "institute",
        "instituteAddress",
        "sport",
        "player",
        "number",
        "contact",
        "role",
        "captainName",
        "captainEmail",
        "captainContact",
        "viceCaptainName",
        "viceCaptainEmail",
        "viceCaptainContact",
      ],
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "players_export.csv";
    link.click();
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <ToastContainer />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-700">Users & Players Data</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleLogoutClick}
            className="px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700"
          >
            Logout
          </button>
          <label className="text-gray-600 font-medium">Select Collection:</label>
          <select
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            className="px-4 py-2 border rounded-lg shadow bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="Users24">Users24</option>
            <option value="Users25">Users25</option>
          </select>
        </div>
      </div>

      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab("users")}
          className={`px-6 py-2 rounded-lg font-semibold shadow ${activeTab === "users"
            ? "bg-blue-600 text-white"
            : "bg-white text-gray-700 border border-gray-300 hover:bg-blue-50"
            } transition`}
        >
          Users Table
        </button>
        <button
          onClick={() => setActiveTab("players")}
          className={`px-6 py-2 rounded-lg font-semibold shadow ${activeTab === "players"
            ? "bg-green-600 text-white"
            : "bg-white text-gray-700 border border-gray-300 hover:bg-green-50"
            } transition`}
        >
          Players Table
        </button>
      </div>

      {activeTab === "users" && (
        <div className="bg-white rounded-xl shadow mb-10 transition flex flex-col max-h-[70vh]">
          <div className="flex justify-between items-center p-4 border-b bg-white sticky top-0 z-20">
            <h2 className="text-xl font-bold text-gray-700">Users Data</h2>
            <button
              onClick={exportCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700"
            >
              Export Users CSV
            </button>
          </div>

          <div className="overflow-auto flex-1">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gradient-to-r from-blue-600 to-blue-600 text-white">
                  {headers.map((header) => (
                    <th key={header} className="px-4 py-3 text-left">
                      {header.toUpperCase()}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left">CONFIRMED</th>
                  <th className="px-4 py-3 text-left">RESPONSE</th>
                  <th className="px-4 py-3 text-left">POC</th>
                  <th className="px-4 py-3 text-left">ACTIONS</th>
                </tr>
                <tr className="bg-gray-100">
                  {headers.map((header) => (
                    <th key={header} className="px-4 py-2">
                      <input
                        type="text"
                        placeholder={`Filter ${header}`}
                        value={filters[header] || ""}
                        onChange={(e) => handleFilterChange(header, e.target.value)}
                        className="w-full font-normal px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm"
                      />
                    </th>
                  ))}
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`border-b ${rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"
                        } hover:bg-gray-100 transition`}
                    >
                      {headers.map((header) => {
                        if (
                          header.toLowerCase() === "sports" &&
                          row[header] &&
                          typeof row[header] === "object"
                        ) {
                          return (
                            <td key={header} className="px-4 py-2">
                              <button
                                onClick={() => handleViewSports(row[header], row.id)}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs hover:bg-blue-200 transition"
                              >
                                View Sports
                              </button>
                            </td>
                          );
                        }
                        return (
                          <td key={header} className="px-4 py-2 text-sm">
                            {String(row[header] ?? "")}
                          </td>
                        );
                      })}
                      
                      <td className="px-4 py-2">
                        <button
                          onClick={() => toggleYesNo(row.id, row.confirmed || false)}
                          className={`px-3 py-1 rounded text-xs font-medium transition ${
                            row.confirmed 
                              ? "bg-green-600 text-white hover:bg-green-700" 
                              : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                          }`}
                        >
                          {row.confirmed ? "Yes" : "No"}
                        </button>
                      </td>
                      
                      <td className="px-4 py-2">
                        {editingField[row.id]?.response ? (
                          <div className="flex items-center">
                            <input
                              type="text"
                              value={tempValues[row.id]?.response || ""}
                              onChange={(e) => handleResponseChange(row.id, e.target.value)}
                              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                              placeholder="Enter response"
                            />
                            <button
                              onClick={() => saveResponse(row.id)}
                              className="ml-2 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => cancelEditing(row.id, 'response')}
                              className="ml-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span className="flex-1">{row.response || ""}</span>
                            <button
                              onClick={() => startEditing(row.id, 'response')}
                              className="ml-2 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => clearResponse(row.id)}
                              className="ml-1 px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 transition"
                              title="Clear Response"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </td>
                      
                      <td className="px-4 py-2">
                        {editingField[row.id]?.poc ? (
                          <div className="flex items-center">
                            <input
                              type="text"
                              value={tempValues[row.id]?.poc || ""}
                              onChange={(e) => handlePocChange(row.id, e.target.value)}
                              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                              placeholder="Enter POC"
                            />
                            <button
                              onClick={() => savePoc(row.id)}
                              className="ml-2 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => cancelEditing(row.id, 'poc')}
                              className="ml-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span className="flex-1">{row.poc || ""}</span>
                            <button
                              onClick={() => startEditing(row.id, 'poc')}
                              className="ml-2 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => clearPoc(row.id)}
                              className="ml-1 px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 transition"
                              title="Clear POC"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </td>
                      
                      <td className="px-4 py-2">
                        <button
                          onClick={() => deleteUser(row.id)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={headers.length + 4}
                      className="text-center py-6 text-gray-500"
                    >
                      No matching records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "players" && (
        <div className="bg-white rounded-xl shadow transition flex flex-col max-h-[70vh]">
          <div className="flex justify-between items-center p-4 border-b bg-white sticky top-0 z-20">
            <h3 className="text-xl font-semibold text-gray-700">Players Data</h3>
            <button
              onClick={exportPlayersCSV}
              className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700"
            >
              Export Players CSV
            </button>
          </div>

          <div className="overflow-auto flex-1">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gradient-to-r from-green-600 to-green-600 text-white">
                  {[
                    "User ID",
                    "Institute",
                    "Institute Address",
                    "Sport",
                    "Player",
                    "Number",
                    "Role",
                    "Captain Name",
                    "Captain Email",
                    "Captain Contact",
                    "Vice Captain Name",
                    "Vice Captain Email",
                    "Vice Captain Contact",
                    "Actions"
                  ].map((header) => (
                    <th key={header} className="px-4 py-2">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {playersData.length > 0 ? (
                  playersData.map((player, idx) => (
                    <tr
                      key={idx}
                      className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                        } hover:bg-gray-100 transition border-b`}
                    >
                      <td className="px-4 py-2">{player.userId}</td>
                      <td className="px-4 py-2">{player.institute}</td>
                      <td className="px-4 py-2">{player.instituteAddress}</td>
                      <td className="px-4 py-2">{player.sport}</td>
                      <td className="px-4 py-2">{player.player}</td>
                      <td className="px-4 py-2">{player.number}</td>
                      <td className="px-4 py-2">{player.role}</td>
                      <td className="px-4 py-2">{player.captainName}</td>
                      <td className="px-4 py-2">{player.captainEmail}</td>
                      <td className="px-4 py-2">{player.captainContact}</td>
                      <td className="px-4 py-2">{player.viceCaptainName}</td>
                      <td className="px-4 py-2">{player.viceCaptainEmail}</td>
                      <td className="px-4 py-2">{player.viceCaptainContact}</td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => deletePlayer(player)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="14" className="text-center py-6 text-gray-500">
                      No players found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedSports && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-3/4 max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-700">
                Sports Details
              </h3>
              <div className="flex space-x-3">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={saveChanges}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditedSports(JSON.parse(JSON.stringify(selectedSports)));
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg shadow hover:bg-gray-700 transition"
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setSelectedSports(null);
                    setSelectedSportsUserId(null);
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 transition"
                >
                  Close
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {Object.entries(editedSports || selectedSports).map(([sportName, sportInfo]) => (
                <div
                  key={sportName}
                  className="p-4 border rounded-lg shadow-sm hover:shadow-md transition bg-gray-50"
                >
                  <h4 className="text-xl font-semibold mb-3 text-indigo-700">
                    {sportName}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div className="p-2 bg-indigo-100 rounded-md">
                      <p className="font-medium text-indigo-900">Captain</p>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            value={sportInfo.firstNameCaptain || ""}
                            onChange={(e) => handleEditChange(sportName, 'firstNameCaptain', e.target.value)}
                            className="w-full p-1 mb-1 border rounded"
                            placeholder="First Name"
                          />
                          <input
                            type="text"
                            value={sportInfo.lastNameCaptain || ""}
                            onChange={(e) => handleEditChange(sportName, 'lastNameCaptain', e.target.value)}
                            className="w-full p-1 mb-1 border rounded"
                            placeholder="Last Name"
                          />
                          <input
                            type="email"
                            value={sportInfo.emailCaptain || ""}
                            onChange={(e) => handleEditChange(sportName, 'emailCaptain', e.target.value)}
                            className="w-full p-1 mb-1 border rounded"
                            placeholder="Email"
                          />
                          <input
                            type="text"
                            value={sportInfo.contactCaptain || ""}
                            onChange={(e) => handleEditChange(sportName, 'contactCaptain', e.target.value)}
                            className="w-full p-1 border rounded"
                            placeholder="Contact"
                          />
                        </>
                      ) : (
                        <>
                          <p>
                            {sportInfo.firstNameCaptain} {sportInfo.lastNameCaptain}
                          </p>
                          <p>Email: {sportInfo.emailCaptain}</p>
                          <p>Contact: {sportInfo.contactCaptain}</p>
                        </>
                      )}
                    </div>
                    <div className="p-2 bg-indigo-50 rounded-md">
                      <p className="font-medium text-indigo-900">Vice Captain</p>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            value={sportInfo.firstNameViceCaptain || ""}
                            onChange={(e) => handleEditChange(sportName, 'firstNameViceCaptain', e.target.value)}
                            className="w-full p-1 mb-1 border rounded"
                            placeholder="First Name"
                          />
                          <input
                            type="text"
                            value={sportInfo.lastNameViceCaptain || ""}
                            onChange={(e) => handleEditChange(sportName, 'lastNameViceCaptain', e.target.value)}
                            className="w-full p-1 mb-1 border rounded"
                            placeholder="Last Name"
                          />
                          <input
                            type="email"
                            value={sportInfo.emailViceCaptain || ""}
                            onChange={(e) => handleEditChange(sportName, 'emailViceCaptain', e.target.value)}
                            className="w-full p-1 mb-1 border rounded"
                            placeholder="Email"
                          />
                          <input
                            type="text"
                            value={sportInfo.contactViceCaptain || ""}
                            onChange={(e) => handleEditChange(sportName, 'contactViceCaptain', e.target.value)}
                            className="w-full p-1 border rounded"
                            placeholder="Contact"
                          />
                        </>
                      ) : (
                        <>
                          <p>
                            {sportInfo.firstNameViceCaptain}{" "}
                            {sportInfo.lastNameViceCaptain}
                          </p>
                          <p>Email: {sportInfo.emailViceCaptain}</p>
                          <p>Contact: {sportInfo.contactViceCaptain}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <h5 className="font-medium mb-2">Team Members:</h5>
                    {isEditing ? (
                      <div className="space-y-2">
                        {sportInfo.team?.map((player, idx) => (
                          <div key={idx} className="bg-white p-2 rounded shadow-sm grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={player.player || ""}
                              onChange={(e) => handleEditChange(sportName, 'player', e.target.value, true, idx)}
                              className="p-1 border rounded"
                              placeholder="Player Name"
                            />
                            <input
                              type="text"
                              value={player.number || ""}
                              onChange={(e) => handleEditChange(sportName, 'number', e.target.value, true, idx)}
                              className="p-1 border rounded"
                              placeholder="Number"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ul className="list-disc pl-6 space-y-1">
                        {sportInfo.team?.map((player, idx) => (
                          <li
                            key={idx}
                            className="bg-white p-2 rounded shadow-sm"
                          >
                            {player.player} – {player.number}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersTable;